import { Clipboard } from '@angular/cdk/clipboard';
import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, computed, effect, ElementRef, inject, OnDestroy, OnInit, Signal, signal, ViewChild, WritableSignal } from '@angular/core';
import { FormBuilder, FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatToolbarModule } from '@angular/material/toolbar';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { UserAvatarComponent } from 'projects/ui-library/src/lib/components/user-avatar/user-avatar.component';
import QRCode from 'qrcode';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Pop } from 'src/app/classes/shared/pop';
import { MatSelectFixDirective } from 'src/app/directives/matSelectFix.directive';
import { PostEvent } from 'src/app/interfaces/api/event';
import { Project } from 'src/app/interfaces/api/project';
import { AuthMethod, User, UserPersonalization } from 'src/app/interfaces/api/user';
import { EventService } from 'src/app/services/api/events/event.service';
import { ProjectService } from 'src/app/services/api/projects/project.service';
import { UserService } from 'src/app/services/api/users/user.service';
import { AuthService } from 'src/app/services/core/auth.service';
import { ThemeService } from 'src/app/services/core/theme/theme.service';
import { ButtonComponent, ChipComponent } from 'ui-library';
import { EditEmailComponent } from './edit-email/edit-email.component';
import { EditPasswordComponent } from './edit-password/edit-password.component';

type SettingsSectionId = 'info' | 'personalization' | 'security' | 'reports' | 'apikey' | 'debug';
type SettingsGroupId = 'account' | 'preferences' | 'system';

interface SettingsNavItem {
  id: SettingsSectionId;
  icon: string;
  labelKey: string;
  group: SettingsGroupId;
  badgeKey?: string;
}

interface SettingsNavGroup {
  key: SettingsGroupId;
  labelKey: string;
  sections: SettingsNavItem[];
}

interface ThemeOption {
  id: string;
  labelKey: string;
  previewClass: string;
  family: 'light' | 'dark';
}

const SETTINGS_SECTIONS: SettingsNavItem[] = [
  { id: 'info', icon: 'badge', labelKey: 'settings.project.informations', group: 'account' },
  { id: 'security', icon: 'shield', labelKey: 'settings.project.security', group: 'account' },
  { id: 'apikey', icon: 'vpn_key', labelKey: 'settings.project.api_key', group: 'account' },
  { id: 'personalization', icon: 'palette', labelKey: 'settings.project.personalization', group: 'preferences', badgeKey: 'settings.project.new_badge' },
  { id: 'reports', icon: 'bar_chart', labelKey: 'settings.project.reports', group: 'preferences' },
  { id: 'debug', icon: 'terminal', labelKey: 'settings.project.dev_tools', group: 'system' },
];

const SETTINGS_GROUPS: Array<{ key: SettingsGroupId; labelKey: string }> = [
  { key: 'account', labelKey: 'settings.project.account_group' },
  { key: 'preferences', labelKey: 'settings.project.preferences_group' },
  { key: 'system', labelKey: 'settings.project.system_group' },
];

const THEME_OPTIONS: ThemeOption[] = [
  { id: 'light', labelKey: 'settings.project.profile.lightTheme', previewClass: 'light-preview', family: 'light' },
  { id: 'paper', labelKey: 'settings.project.profile.paperTheme', previewClass: 'paper-preview', family: 'light' },
  { id: 'dark', labelKey: 'settings.project.profile.darkTheme', previewClass: 'dark-preview', family: 'dark' },
  { id: 'midnight', labelKey: 'settings.project.profile.midnightTheme', previewClass: 'midnight-preview', family: 'dark' },
];
const DEFAULT_THEME_ID = 'dark';

const ALLOWED_DEBUG_EMAILS = [
  'mcazelle@cad42.com',
  'flborgne@cad42.com',
  'gdadaev@cad42.com'
];

@Component({
  selector: 'app-profile',
  templateUrl: 'profile.component.html',
  styleUrls: ['./profile.component.scss'],
  standalone: true,
  imports: [
    ButtonComponent,
    ChipComponent,
    MatToolbarModule,
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSlideToggleModule,
    MatSelectModule,
    MatCheckboxModule,
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    TranslateModule,
    MatButtonModule,
    MatSelectFixDirective,
    UserAvatarComponent
  ]
})

export class ProfileComponent implements OnInit, OnDestroy {
  // Services
  private readonly authService: AuthService = inject(AuthService);
  private readonly projectService: ProjectService = inject(ProjectService);
  private readonly userService: UserService = inject(UserService);
  private readonly router: Router = inject(Router);
  private readonly translate: TranslateService = inject(TranslateService)
  private readonly eventService: EventService = inject(EventService);
  private readonly dialog: MatDialog = inject(MatDialog);
  private readonly formBuilder: FormBuilder = inject(FormBuilder);
  private readonly pop: Pop = inject(Pop);
  private readonly themeService: ThemeService = inject(ThemeService);
  private readonly cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  private readonly clipboard: Clipboard = inject(Clipboard);

  // Components API
  @ViewChild('avatarInput', { static: false }) avatarInput!: ElementRef;

  // Signals
  readonly userSignal: Signal<User> = this.authService.currentUser;
  readonly project: Signal<Project> = this.projectService.currentProjectSignal;
  readonly activeSection: WritableSignal<SettingsSectionId> = signal('info');

  // Computeds
  readonly userRole = computed(() => this.authService.currentUser().role?.name);
  readonly currentThemeId = computed(() => this.themeService.theme().replace(/-theme$/, '') || DEFAULT_THEME_ID);

  readonly isDarkTheme = computed(() =>
    (THEME_OPTIONS.find(opt => opt.id === this.currentThemeId())?.family ?? 'dark') === 'dark'
  );

  readonly projectsView = computed(() => this.userSignal()?.settings?.projectsView ?? 'card');
  readonly showArchivedProjectsByDefault = computed(() => this.userSignal()?.settings?.showArchivedProjects ?? false);

  readonly themeOptions = THEME_OPTIONS;
  readonly canShowDebugTab = computed(() => ALLOWED_DEBUG_EMAILS.includes(this.userSignal()?.email));

  readonly visibleSections = computed(() =>
    SETTINGS_SECTIONS.filter(section => section.id !== 'debug' || this.canShowDebugTab())
  );

  readonly groupedSections = computed<SettingsNavGroup[]>(() => {
    const byGroup = new Map<SettingsGroupId, SettingsNavItem[]>();

    for (const section of this.visibleSections()) {
      const bucket = byGroup.get(section.group) ?? [];
      bucket.push(section);
      byGroup.set(section.group, bucket);
    }

    return SETTINGS_GROUPS
      .map(group => ({ ...group, sections: byGroup.get(group.key) ?? [] }))
      .filter(group => group.sections.length > 0);
  });

  readonly activeSectionMeta = computed(() => this.visibleSections().find(s => s.id === this.activeSection()));

  // Variables
  account: any = {};
  userPwd: any;
  avatar: string = "/img/default-avatar.png";
  avatarUpdated = false;
  user!: User;
  description: string = "";
  form: any;
  error!: string;
  selectedPhoto: any;
  loadingAvatar: number | null = null;
  savingProfile: boolean = false;
  currentImage: any;
  language!: string;
  selected: any;
  title!: string;
  defaultLang: any;
  listCase: any = [{ name: 'kyd', value: 'kyd' }, { name: 'gps', value: 'gps' }]
  chooseHome: any[] = [];
  defaultCase: any;
  useCase!: string;
  listLanguage: any = [];
  _chooseHome: any;
  matches: Array<String> = [];
  systemVolume: number = 0;
  connectSubscription: any;
  updateEmail: boolean = false;
  currentProject: any = {
    reportSettings: {}
  };
  destroy$ = new Subject<void>();
  projectsColumns: string[] = ['firstName', 'name', 'email', 'action'];
  projectsCount: number = 0;
  addToProjectLists: string[] = [];
  removeFromProjectLists: string[] = [];
  subscribedTo: any[] = []; // [{ id: "sds", name: "Dev", inMailingList: true }, { id: "dsds", name: "Demo", inMailingList: false }];
  initSubscribedTo: any[] = [];
  listLanguageOptions: any = [];
  roleChanged: boolean = false;
  debugRoles: string[] = ['dev', 'admin', 'userAdmin', 'user', 'guest'];
  mfaEnabled: boolean = false;
  activeMfaMethod: 'email' | 'totp' | null = null;
  savingMfa: boolean = false;
  pendingFlow: 'enroll-select' | 'enroll' | 'disable' | null = null;
  pendingToken: string = '';
  pendingCode: string = '';
  pendingMfaMethod: 'email' | 'totp' | null = null;
  pendingQrCodeUrl: string | null = null;
  pendingSecret: string | null = null;
  savingPending: boolean = false;
  pendingError: string | null = null;
  pendingTimeLeft: number = 0;
  private pendingTimer: any;

  constructor() {
    effect(() => {
      const user = this.userSignal();

      if (!user || !user.id) return;

      this.user = user;
      this.avatar = user.avatar || "/img/default-avatar.png";

      // Langue et thème par défaut
      if (user.language && user.theme) {
        this.defaultLang = user.language;
        this.translate.setDefaultLang(this.defaultLang);
        this.account.language = this.defaultLang;
      } else {
        this.defaultLang = this.userService.initLanguage();
        this.translate.setDefaultLang(this.defaultLang);
        this.account.language = this.defaultLang;
      }

      // Liste des langues
      this.listLanguageOptions = [
        { label: 'Francais', value: 'fr-FR' },
        { label: 'English', value: 'en-US' }
      ];

      if (user.avatar) {
        this.avatar = user.avatar;
      }

      if (this.defaultLang === 'en-US')
        this.listLanguage = [{ name: 'French', value: 'fr-FR' }, { name: 'English', value: 'en-US' }]
      else
        this.listLanguage = [{ name: 'Français', value: 'fr-FR' }, { name: 'Anglais', value: 'en-US' }];

      // Gestion subscribeToReport
      if (Array.isArray(user.settings["subscribeToReport"]) && user.settings["subscribeToReport"].length == 1) {
        user.settings["subscribeToReport"] = user.settings["subscribeToReport"][0];
      }

      switch (user.settings["subscribeToReport"]) {
        case "all":
          user.settings["subscribeToReport"] = ["daily", "weekly"];
          break;
        case "daily":
        case "weekly":
          user.settings["subscribeToReport"] = [user.settings["subscribeToReport"]];
          break;
        case "never":
        case undefined:
        case "":
          user.settings = { ...user.settings, subscribeToReport: [] }
          break;
      }

      if (typeof user.settings["subscribeToReport"] == "string")
        user.settings["subscribeToReport"] = [user.settings["subscribeToReport"]];

      this.initForm(user);
      this.getProjectMailingLists();
      this.cdr.markForCheck();
    });
  }

  ngOnInit() {
    this.userService.getSecuritySettings().pipe(takeUntil(this.destroy$)).subscribe({
      next: (settings) => {
        this.mfaEnabled = settings.mfaEnabled;
        this.activeMfaMethod = settings.mfaMethod ?? null;
        this.cdr.markForCheck();
      },
      error: () => { }
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    clearInterval(this.pendingTimer);
  }

  initForm(user: User) {
    this.form = this.formBuilder.group({
      avatar: [''],
      firstName: [user.firstName, Validators.required],
      lastName: [user.lastName, Validators.required],
      job: [user.job, Validators.required],
      company: [user.company, Validators.required],
      language: [user.language, Validators.required],
      email: new FormControl({ value: user.email, disabled: true }),
      subscribeToReport: [user.settings['subscribeToReport']],
      password: new FormControl({ value: '******', disabled: true }),
      theme: [user.theme || DEFAULT_THEME_ID],
    });
  }

  changeLanguage(lang: string): void {
    this.translate.use(lang);
    this.userService.setLanguage(lang)
  }

  getProjectMailingLists() {
    if (!this.user || !this.user.id) return;

    this.addToProjectLists = [];
    this.removeFromProjectLists = [];
    let query = { users: [this.user.id], byUsers: true };

    this.projectService.getProjects(query)
      .pipe(takeUntil(this.destroy$))
      .subscribe(projects => {
        this.subscribedTo = projects.map((p: Project) => ({
          id: p.id,
          name: p.name,
          inMailingList: p.mailingList?.includes(this.user.id)
        }));

        this.subscribedTo.sort((p1, p2) => p1.name > p2.name ? 1 : 1);
        this.initSubscribedTo = this.subscribedTo.map(p => ({ ...p }));
      });
  }


  validText(text: string, pattern: string) {
    return String(text).match(pattern);
  }

  logout() {
    let event: PostEvent = {
      title: 'user logout',
      type: 'Tracker log',
      startDate: moment().toISOString(),
      user: this.user.id,
      project: this.project()?.id || '',
      trackerName: this.user.email
    }

    this.eventService.addEvent(event).pipe(takeUntil(this.destroy$)).subscribe(res => {
      console.log('success request');
    }, err => console.error(err));

    this.authService.logout();
  }

  support() {
    this.router.navigateByUrl('/support');
  }

  reportList() {
    this.router.navigateByUrl('/reports')
  }

  updateProfile() {
    if (this.roleChanged) {
      this.userService.setSelfRole(this.user.role.name).pipe(takeUntil(this.destroy$)).subscribe({
        next: () => {
          this.authService.setUser(this.user);
          this.roleChanged = false;
        },
        error: (err: any) => {
          this.pop.error(this.translate.instant('settings.project.profile.save_error_profil') + ' : ' + err.message);
        }
      });
    }

    if (this.form.valid && this.form.dirty) {
      this.savingProfile = true;
      let lang = this.form.value.language;

      let user: any = {
        id: this.user.id,
        firstName: this.form.value.firstName,
        lastName: this.form.value.lastName,
        job: this.form.value.job,
        company: this.form.value.company,
        language: this.form.value.language,
        email: this.form.get('email').value,
        settings: {
          ...this.user.settings,
          subscribeToReport: this.form.value.subscribeToReport
        },
        theme: this.form.value.theme,
        accessKey: this.user.accessKey,
        subscriptions: this.user.subscriptions
      }

      if (this.avatarUpdated) {
        user.avatar = this.avatar;
      }

      this.themeService.setTheme(user.theme + '-theme');

      if (this.userPwd)
        user.password = this.userPwd;

      this.translate.use(lang);
      this.userService.setLanguage(lang);

      this.userService.setUser(user).pipe(takeUntil(this.destroy$)).subscribe({
        next: (res: any) => {
          const updatedUser = {
            ...this.user,
            ...user,
            avatar: res.avatar
          };

          this.authService.setUser(updatedUser);
          this.form.markAsPristine();
          this.avatarUpdated = false;
          this.userPwd = undefined;

          if (this.mailingListChanged()) {
            this.updateMailingLists();
          } else {
            this.pop.toast(this.translate.instant('settings.project.profile.save_success_profil'), "success", 4000, 'save');
            this.savingProfile = false;
          }
        },
        error: (err: any) => {
          this.pop.error(this.translate.instant('settings.project.profile.save_error_profil') + ' : ' + err.message);
          this.savingProfile = false;
        }
      });
    } else if (this.mailingListChanged()) {
      this.updateMailingLists();
    }
  }

  editEmail() {
    const modal = this.dialog.open(EditEmailComponent, {
      width: '600px',
      data: { email: this.userSignal().email }
    });

    modal.afterClosed().subscribe(rs => {
      if (!rs) {
        this.updateEmail = true;
        this.form.get('email').enable(true);
      }
    })
  }

  editPassword() {
    const modal = this.dialog.open(EditPasswordComponent, {
      width: '600px',
      data: { email: this.userSignal().email }
    })

    modal.afterClosed().subscribe(res => {
      if (res != 'false') {
        this.userPwd = res;
        this.form.markAsDirty();
        this.form.markAllAsTouched();
      }
    })
  }

  resetPassword() {
    this.userPwd = '';
  }

  genKey() {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    const key = Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');

    this.user.accessKey = key
    this.form.markAsDirty();
    this.form.markAllAsTouched();
  }

  copy(content: string | undefined) {
    if (content)
      this.clipboard.copy(content);
  }

  selectTheme(themeId: string) {
    if (this.currentThemeId() === themeId) return;

    this.themeService.setTheme(themeId + '-theme');
    this.form.get('theme')?.setValue(themeId);
    this.form.get('theme')?.markAsDirty();
  }

  setProjectsView(view: 'card' | 'list') {
    if (this.projectsView() === view) return;

    this.savePersonalization({ projectsView: view });
  }

  toggleShowArchivedProjectsByDefault(checked: boolean) {
    this.savePersonalization({ showArchivedProjects: checked });
  }

  private savePersonalization(personalization: Partial<UserPersonalization>) {
    this.userService.updatePersonalization(personalization).pipe(takeUntil(this.destroy$)).subscribe({
      error: () => this.pop.error(this.translate.instant('settings.project.profile.personalization_save_error'))
    });
  }

  isAuthMethodAllowed(method: AuthMethod): boolean {
    return this.user?.allowedAuthMethods?.includes(method) ?? true;
  }

  toggleMfa(enabled: boolean) {
    if (enabled) {
      this.pendingFlow = 'enroll-select';
      this.pendingCode = '';
      this.pendingError = null;
    } else {
      this.startDisableFlow();
    }
  }

  selectEnrollMethod(method: 'email' | 'totp') {
    this.pendingMfaMethod = method;
    this.savingMfa = true;

    this.userService.startMfaEnrollment(method).pipe(takeUntil(this.destroy$)).subscribe({
      next: async (res) => {
        this.pendingToken = res.enrollToken;
        this.pendingCode = '';
        this.pendingError = null;
        this.pendingQrCodeUrl = null;
        this.pendingSecret = null;

        if (res.qrCodeUri) {
          this.pendingQrCodeUrl = await QRCode.toDataURL(res.qrCodeUri);
          try {
            this.pendingSecret = new URL(res.qrCodeUri).searchParams.get('secret');
          } catch { }
        }

        this.pendingFlow = 'enroll';
        this.startPendingTimer(res.expiresAt);
        this.savingMfa = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.pop.error(this.translate.instant('settings.project.profile.mfa_error'));
        this.pendingFlow = null;
        this.savingMfa = false;
      }
    });
  }

  private startDisableFlow() {
    this.savingMfa = true;

    this.userService.startMfaDisable().pipe(takeUntil(this.destroy$)).subscribe({
      next: (res: any) => {
        this.pendingToken = res.disableToken;
        this.pendingMfaMethod = res.mfaMethod as 'email' | 'totp';
        this.pendingCode = '';
        this.pendingError = null;
        this.pendingFlow = 'disable';
        this.startPendingTimer(res.expiresAt);
        this.savingMfa = false;
      },
      error: (err: any) => {
        const key = err?.error?.error === 'mfa_not_enabled'
          ? 'settings.project.profile.mfa_not_enabled'
          : 'settings.project.profile.mfa_error';
        this.pop.error(this.translate.instant(key));
        this.savingMfa = false;
      }
    });
  }

  private startPendingTimer(expiresAt: string) {
    clearInterval(this.pendingTimer);
    const tick = () => {
      this.pendingTimeLeft = Math.max(0, Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000));
      if (this.pendingTimeLeft === 0) clearInterval(this.pendingTimer);
      this.cdr.markForCheck();
    };
    tick();
    this.pendingTimer = setInterval(tick, 1000);
  }

  confirmPending() {
    if (this.pendingCode.length !== 6 || this.savingPending || !this.pendingFlow) return;
    this.savingPending = true;
    this.pendingError = null;

    const request$ = this.pendingFlow === 'enroll'
      ? this.userService.confirmMfaEnrollment(this.pendingToken, this.pendingCode)
      : this.userService.confirmMfaDisable(this.pendingToken, this.pendingCode);

    const successEnabled = this.pendingFlow === 'enroll';
    const maxAttemptsCode = this.pendingFlow === 'enroll' ? 'enrollment_max_attempts' : 'disable_max_attempts';

    request$.pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.mfaEnabled = successEnabled;
        this.activeMfaMethod = successEnabled ? this.pendingMfaMethod : null;
        this.authService.setUser({ ...this.user, mfaEnabled: successEnabled });
        clearInterval(this.pendingTimer);
        this.pendingFlow = null;
        const key = successEnabled
          ? 'settings.project.profile.mfa_enabled_success'
          : 'settings.project.profile.mfa_disabled_success';
        this.pop.toast(this.translate.instant(key), 'success', 4000, 'security');
        this.savingPending = false;
      },
      error: (err) => {
        const code = err?.error?.error;
        if (code === maxAttemptsCode) {
          this.cancelPending();
          this.pop.error(this.translate.instant('settings.project.profile.mfa_max_attempts'));
        } else {
          this.pendingError = this.translate.instant('settings.project.profile.mfa_invalid_code');
          this.pendingCode = '';
        }
        this.savingPending = false;
      }
    });
  }

  cancelPending() {
    this.pendingFlow = null;
    this.pendingCode = '';
    this.pendingError = null;
    this.pendingTimeLeft = 0;
    this.pendingMfaMethod = null;
    this.pendingQrCodeUrl = null;
    this.pendingSecret = null;
    clearInterval(this.pendingTimer);
  }

  updateMailingLists() {
    this.projectService.updateMailingLists(this.addToProjectLists, this.removeFromProjectLists).pipe(takeUntil(this.destroy$))
      .subscribe(res => {
        this.initSubscribedTo = this.subscribedTo.map(p => ({ ...p }));
        this.addToProjectLists = [];
        this.removeFromProjectLists = [];
        this.pop.toast(this.translate.instant('settings.project.profile.save_success_profil'), "success", 4000, 'save');
        this.savingProfile = false;
      })
  }

  mailingListChanged() {
    return this.addToProjectLists?.length || this.removeFromProjectLists?.length;
  }

  toggleSubscription($event: MatCheckboxChange, projectId: string) {
    if (!this.user.subscriptions)
      this.user.subscriptions = [];

    let subscripIndex = this.user.subscriptions.findIndex((s: any) => s.project == projectId);

    const subscribeToReport = this.user.settings["subscribeToReport"] ?? [];
    let dailyReport = subscribeToReport.indexOf("daily") >= 0 || subscribeToReport.indexOf("all") >= 0;
    let weeklyReport = subscribeToReport.indexOf("weekly") >= 0 || subscribeToReport.indexOf("all") >= 0;
    let index = this.subscribedTo.findIndex(p => p.id == projectId);

    if (index >= 0) {
      let hasSubscribed = this.subscribedTo[index].inMailingList;
      this.subscribedTo[index].inMailingList = !hasSubscribed;
      this.addToProjectLists = [];
      this.removeFromProjectLists = [];
      let inMailingList = this.subscribedTo[index].inMailingList;

      if (subscripIndex == -1) {
        this.user.subscriptions.push({
          email: inMailingList,
          dailyReport,
          weeklyReport,
          project: projectId
        });
      } else if (subscripIndex >= 0) {
        this.user.subscriptions[subscripIndex] = {
          project: projectId,
          email: inMailingList,
          dailyReport,
          weeklyReport
        }
      }

      this.subscribedTo.forEach((_project, i) => {
        if (_project.inMailingList && !this.initSubscribedTo[i].inMailingList)
          this.addToProjectLists.push(_project.id);

        if (!_project.inMailingList && this.initSubscribedTo[i].inMailingList)
          this.removeFromProjectLists.push(_project.id);
      });
    }
  }

  changeRole(role: 'dev' | 'admin' | 'userAdmin' | 'user' | 'guest') {
    if (!this.canShowDebugTab()) return;

    if (this.user.role.name !== role) {
      this.user.role.name = role;
      this.roleChanged = true;
      this.form.markAsDirty();
    }
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;

    if (input.files && input.files[0]) {
      const file = input.files[0];

      if (!this.validateImageFile(file)) {
        return;
      }

      const reader = new FileReader();

      reader.onprogress = (e: ProgressEvent<FileReader>) => {
        if (e.lengthComputable) {
          const percentLoaded = Math.round((e.loaded / e.total) * 100);
          this.loadingAvatar = percentLoaded;
        }
      };

      reader.onloadstart = () => {
        this.loadingAvatar = 0;
      };

      reader.onload = (e: ProgressEvent<FileReader>) => {
        if (e.target && e.target.result) {
          const url = e.target.result;
          this.avatar = url.toString();
          this.user.avatar = this.avatar;
          this.avatarUpdated = true;

          this.loadingAvatar = 100;

          setTimeout(() => {
            this.loadingAvatar = null;
            this.cdr.markForCheck();
          }, 1000);

          this.cdr.markForCheck();
        }
      };

      reader.onerror = (error) => {
        console.error('❌ Erreur lors de la lecture:', error);
        this.pop.error('Erreur lors du chargement de l\'image');
        this.loadingAvatar = null;
      };

      reader.readAsDataURL(file);
    }
  }

  private validateImageFile(file: File): boolean {
    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];

    if (!allowedTypes.includes(file.type)) {
      console.error('❌ Type de fichier non supporté:', file.type);
      this.pop.error('Format d\'image non supporté. Utilisez JPG, PNG, GIF ou WebP.');
      return false;
    }

    if (file.size > maxSize) {
      console.error('❌ Fichier trop volumineux:', `${(file.size / 1024 / 1024).toFixed(2)} MB`);
      this.pop.error('L\'image est trop volumineuse (max 5MB)');
      return false;
    }

    return true;
  }

  deleteAvatar() {
    this.avatar = "";
    this.user.avatar = "";
    this.avatarUpdated = true;

    this.form.markAsDirty();
    this.form.get('avatar')?.markAsDirty();
    this.form.get('avatar')?.updateValueAndValidity();
  }
}
